// Flicker proof graph — the math behind the before/after curves (#405).
//
// WHAT THIS IS
// ------------
// "Fix flicker" is the one operation nobody can verify from a thumbnail, and
// today the only way to check it is to pay for a full render. This module turns
// the luminance track the analyzer already stored (#402) into a PREDICTION of
// what the deflicker filter does to frame brightness, computed entirely in the
// browser: zero ffmpeg, zero worker, zero credits.
//
// The prediction is exact in kind, and deliberately re-timed. The render worker
// ships ffmpeg's `deflicker=size=N:mode=am`, which pulls each frame's mean
// luminance toward the arithmetic mean of an N-frame window, so the "after"
// curve here is the same N-frame arithmetic mean of the same quantity.
//
// ONE DIFFERENCE IS INTENTIONAL. ffmpeg's window is FORWARD-looking: the
// correction for frame i is computed from frames i..i+N-1, which is exactly why
// the render worker reads lookahead frames past every clip (the #382 note in
// generatevideo/processing/video_editing.py). `rollingMean` below is CENTRED.
// The two are the same curve offset by ~N/2 frames — 5 points out of 240, under
// 2% of the chart's width — and generatevideo/benchmark/bench_deflicker.py
// removes precisely that lag before scoring against real ffmpeg output. Centring
// is the right choice HERE because the smooth curve is drawn on top of the
// jagged one it is being compared against: a forward curve would trail the raw
// line at every brightness ramp and read as a rendering bug. The offset is
// pinned by a test in flickerPreview.test.js so it stays a documented choice
// rather than an accident.
//
// It is still a prediction OF MEAN BRIGHTNESS, not a rendered frame — the UI
// copy has to say so, and does.
//
// THE DOWNSAMPLE TRAP (the whole reason this file exists)
// -------------------------------------------------------
// `video.luminanceTrack.luminance` is NOT one value per frame. Firestore
// documents are capped at 1 MiB, so the analyzer cuts the full-rate track into
// at most MAX_TRACK_POINTS blocks and stores each block's MEAN
// (`summarize_luminance_track` in analyzevideo/processing/flicker.py). Every
// stored point therefore already covers `downsampleFactor` source frames.
//
// That breaks the graph in TWO independent ways, and both are handled here.
//
// 1. THE WINDOW IS IN THE WRONG UNITS. A rolling mean over 11 STORED POINTS is
//    not the filter's 11-FRAME window when downsampleFactor > 1 — it is an
//    11 * downsampleFactor frame window, so it would draw a far flatter "after"
//    curve than the render produces. The window has to be converted into points
//    first (`deflickerPointWindow`), and once downsampleFactor reaches the
//    window size that conversion floors at a single point: the identity, which
//    would draw "before" and "after" as one line and tell the user the filter
//    does nothing.
//
// 2. THE "BEFORE" CURVE IS NOT WHAT WAS SHOT — and this is the stricter
//    constraint. Every stored point is a mean of downsampleFactor frames, and a
//    mean is a low-pass filter. Frame-to-frame exposure flicker is the HIGHEST
//    frequency in the signal, so blocking attenuates it hardest: even a
//    2-frame block cancels perfectly alternating flicker outright. Plotting
//    that line under the label "As shot" therefore understates the real flicker
//    and shrinks the visible gap between the curves — it makes Fix flicker look
//    weaker than it is. Verified on screen at downsampleFactor 4, where the two
//    curves nearly coincide even though the prediction is measurably correct.
//
// So the two-curve comparison is drawn ONLY where the record PROVES one stored
// point per frame — stated factor 1 AND sourceFrameCount equal to the point
// count — so that "As shot" is literally true. A missing or under-reported
// factor is not proof, and defaulting it to 1 would hand the misleading chart
// to exactly the records that cannot be checked; the point count measured
// against sourceFrameCount is authoritative whatever the factor field says. At
// every coarser or unprovable resolution this module returns `mode: 'residual'`
// and hands back the stored `flickerPercent` track instead — the one that survives
// blocking, because the analyzer measures it on the FULL-rate track BEFORE
// blocking. Same rule, same reason, whether the storage lost some of the
// flicker or all of it.
//
// THE RESIDUAL "AFTER" LINE COMES FROM THE ANALYZER, NEVER FROM HERE (#405)
// -------------------------------------------------------------------------
// Residual mode used to draw one line — the measured flicker — because the
// same downsample rule that forbids a client-computed "after" LUMINANCE curve
// forbids a client-computed "after" RESIDUAL just as hard: the stored track no
// longer contains the frequency the filter removes, so any smoothing done here
// would be arithmetic on a signal the thing being predicted has already been
// averaged out of. The analyzer now computes that prediction where it is
// honest — at FULL rate, before blocking — and stores it alongside
// `flickerPercent` as `flickerPercentAfter`, reduced with the identical
// statistic and identical blocking so the two arrays share one axis
// (`summarize_luminance_track` in analyzevideo/processing/flicker.py).
//
// This module therefore PASSES THAT ARRAY THROUGH and computes nothing. The
// rule to keep: if the field is absent, malformed, or a different length from
// `flickerPercent`, return null and draw one line. Every project analyzed
// before #405 is in exactly that state, and inventing the line for them is the
// dishonesty this whole file exists to prevent.
//
// The stored prediction carries the same centred-versus-forward offset
// described above: the analyzer models the filter with its centred
// `_local_mean`, so a residual is attributed ~N/2 frames early. It shifts WHERE
// on the timeline the leftover flicker sits, not HOW MUCH of it there is.
//
// TWO WINDOWS, AND ONLY ONE OF THEM MOVES (the Strength control)
// ---------------------------------------------------------------
// The smoothing window used to be one hardcoded 11. It is now two numbers that
// happen to share a value, and conflating them again would break the chart in a
// way no test of a single strength can see:
//
//   * the MEASUREMENT window (`FLICKER_MEASURE_WINDOW_FRAMES`) is what "flicker
//     %" MEANS. The analyzer detrends with it to decide how much of a frame's
//     brightness is jitter rather than trend, so the y-axis of the residual
//     chart is defined by it. It is fixed forever. If it moved, every stored
//     `flickerPercent` in the database would silently change scale and the
//     "noticeable at 1%" reference line would stop meaning anything.
//
//   * the CORRECTION window (`DEFLICKER_WINDOW_CHOICES`, chosen by the user and
//     persisted as `deflickerWindow`) is what the render's
//     `deflicker=size=N:mode=am` actually averages. Only this one varies.
//
// So moving the Strength control changes the AFTER line and nothing else: the
// axis, the measured line, and the threshold all stay put, which is the only
// way the two positions of the control are comparable at a glance.
//
// In comparison mode the after-line is recomputed here, live and free, because
// a proven full-rate track still holds every frequency the filter removes — and
// that mode is where over-smoothing is visible, since a 21-frame window
// noticeably flattens a genuine ramp as well as the jitter. In residual mode
// nothing may be recomputed (see above), so the analyzer stores one blocked
// series PER CHOICE in `flickerPercentAfterByWindow`, keyed by the window as a
// string. The rule that follows from the header's honesty rules: draw the
// series stored under the SELECTED window or draw no after-line at all. A
// series for one window under a label naming another is the exact lie this file
// exists to prevent, and it is invisible on screen — both lines look right.
//
// `flickerPercentAfter` survives as an alias of the "11" series so a client
// deployed ahead of the analyzer (or reading a record written between the two)
// still draws the default correctly. It may ONLY stand in for the default,
// because that is the only window it is known to describe.
//
// FOLLOW-UP WORTH FILING: the two-curve LUMINANCE comparison is still limited
// to sources of at most MAX_TRACK_POINTS frames — `flickerPercentAfter` gives a
// long shoot an honest after-line on the residual axis, but not the before/after
// brightness curves themselves. If the analyzer also stored a short FULL-RATE
// excerpt (say the 240 consecutive frames with the worst residual), the hero
// chart would be honest for a shoot of any length. That is a change to #402's
// stored shape, not to this module.

// The window the ANALYZER detrends with, in SOURCE FRAMES — the one that
// defines what a "flicker %" is. Fixed: it is baked into every `flickerPercent`
// already in the database, so it is not a knob and never becomes one.
//
// WHY THESE NUMBERS ARE TYPED HERE AT ALL. They exist in Python twice
// (generatevideo/processing/video_editing.py, which builds the ffmpeg command,
// and analyzevideo/processing/flicker.py, which detrends and predicts) and
// nowhere in JS. Two routes were possible: persist them into every new
// luminanceTrack record, or keep one JS copy policed by a test. Persisting does
// not remove the hardcode — every project analyzed before that change would
// still fall back to a literal in this file, so it ADDS a copy instead of
// replacing one. This is the single client copy, and flickerPreview.test.js
// parses BOTH Python files and fails if any of them disagree, so drift breaks
// the build instead of silently mis-drawing a curve.
export const FLICKER_MEASURE_WINDOW_FRAMES = 11;

// The CORRECTION windows the user can choose between, in SOURCE FRAMES, and
// what the render's `deflicker=size=N:mode=am` will actually average. Persisted
// on the edit draft as `deflickerWindow`.
//
// Kept odd (the window is centred, so an even one has no middle sample) and
// kept short: 21 frames is already most of a second at 24fps, and past that the
// filter starts eating the slow changes — a sunset ramp — that the operation
// promises to keep. The three are far enough apart to be visibly different on
// the chart, which is the only reason a user would move the control.
export const DEFLICKER_WINDOW_CHOICES = [5, 11, 21];

// What an edit renders with when nobody has touched the control — including
// every draft written before the control existed, which has no `deflickerWindow`
// at all. Equal to the measurement window today; that is a coincidence of
// values, not a shared meaning (see the header), so the two must not be merged.
export const DEFLICKER_WINDOW_DEFAULT = 11;

// FLICKER_DETECT_THRESHOLD (0.010) from analyzevideo/processing/flicker.py,
// expressed in the percent units the track stores. Drawn as a reference line on
// the residual chart so "2.4%" reads as "well over the line" rather than as a
// number with no scale. Pinned by the same drift test.
export const FLICKER_DETECT_THRESHOLD_PERCENT = 1.0;

// Smallest centred window, in STORED POINTS, that still averages anything. A
// 1-point window is the identity function, so a comparison built on it draws
// two identical curves.
export const MIN_COMPARISON_POINT_WINDOW = 3;

// A track shorter than one filter window is not worth drawing: at these
// lengths the analyzer stores one point per frame, so this is a clip of under
// a dozen frames — below MIN_FRAMES_FOR_DETECTION on the Python side too.
//
// Pinned to the MEASUREMENT window, not to whichever correction the user picked:
// otherwise a 15-frame clip would draw a chart at Light and refuse one at
// Strong, and "your footage is too short" would become a statement about a
// setting rather than about the footage.
export const MIN_TRACK_POINTS = FLICKER_MEASURE_WINDOW_FRAMES;

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

// THE ONE STRING GRAMMAR a stored `deflickerWindow` may use, spelled out
// character by character rather than delegated to `\s`/`\d` or to Python's
// `int()`. Its twin is the identical pattern in
// video_editing.py::_coerce_deflicker_window, and they must stay literally the
// same characters — see normalizeDeflickerWindow for what each piece is
// defending against. ASCII whitespace, an optional ASCII sign, 1-9 ASCII
// digits.
//
// THAT AGREEMENT IS TESTED, NOT MERELY DOCUMENTED. deflickerWindow.cases.json
// is ONE physical corpus replayed by BOTH suites — flickerPreview.test.js here,
// and generatevideo/tests/test_video_editing.py against the Python twin — so
// changing this pattern without changing that one reddens the other language
// instead of shipping. New cases go in the corpus, never in one suite's own
// list.
const DEFLICKER_WINDOW_STRING = /^[ \t\n\r\f\v]*[+-]?[0-9]{1,9}[ \t\n\r\f\v]*$/;

/**
 * The user's stored `deflickerWindow` reduced to a window this module will
 * actually draw.
 *
 * THIS MUST AGREE WITH THE WORKER, RULE FOR RULE. The render snaps an
 * unsupported size to the nearest offered one rather than failing
 * (video_editing.py::_coerce_deflicker_window), so a stored 12 RENDERS at 11 —
 * and a reader here that treated 12 as unrecognised would draw the default
 * series for a different reason and be right by accident, while a stored 7
 * would draw 11 and promise a result the render will not deliver. Snapping the
 * same way is what keeps the chart a prediction of THIS project's render.
 *
 * Hence the same rules as its Python twin: a boolean is not a number, a real
 * number truncates toward zero, anything else unparseable or non-positive is
 * the default (the common case — every draft written before the control
 * existed), and ties go to the default (8 is equidistant from 5 and 11 -> 11).
 */
export function normalizeDeflickerWindow(value) {
  // A STRING IS PARSED BY THE SHARED GRAMMAR — NOT by Number(), and NOT by
  // whatever Python's int() happens to take. Each built-in is looser than the
  // other in a different direction, and every one of those gaps is a stored
  // value that would RENDER at one window while this chart drew another:
  //
  //   * Number() reads '0x20' as 32 and '1e2' as 100; int() refuses both, so a
  //     chart using Number() snaps to Strong where the render uses Standard.
  //   * int() accepts any Unicode decimal digit (int('٢١') === 21) and
  //     underscore separators (int('1_0') === 10); JS \d accepts neither, so
  //     the render goes Strong while the chart draws Standard.
  //   * JS \s accepts a BOM that Python's whitespace does not, so '﻿21'
  //     parses here and raises there — the same mislabelling, mirrored.
  //   * Past ~309 digits Number() overflows to Infinity while int() keeps
  //     counting exactly, so a long digit string snaps to Strong there and
  //     falls back to the default here. The 1-9 digit cap in the pattern is
  //     what closes that: nine digits is four orders of magnitude more window
  //     than the largest choice, so anything longer is not a plausible window
  //     in either language.
  //
  // A float still only truncates when it arrives AS a number, matching
  // int(21.9) -> 21 while int('21.9') raises.
  let numeric;
  if (typeof value === 'number') numeric = value;
  else if (typeof value === 'string') {
    numeric = DEFLICKER_WINDOW_STRING.test(value) ? Number(value) : NaN;
  } else numeric = NaN;
  if (!Number.isFinite(numeric)) return DEFLICKER_WINDOW_DEFAULT;
  const truncated = Math.trunc(numeric);
  if (truncated <= 0) return DEFLICKER_WINDOW_DEFAULT;
  return DEFLICKER_WINDOW_CHOICES.reduce((best, choice) => {
    const gap = Math.abs(choice - truncated);
    const bestGap = Math.abs(best - truncated);
    if (gap !== bestGap) return gap < bestGap ? choice : best;
    return Math.abs(choice - DEFLICKER_WINDOW_DEFAULT)
      < Math.abs(best - DEFLICKER_WINDOW_DEFAULT) ? choice : best;
  }, DEFLICKER_WINDOW_DEFAULT);
}

// A finite numeric copy of `values`, or null if it is not a usable track.
// Mirrors `_clean_track` in flicker.py: ANY non-finite sample invalidates the
// whole track rather than being dropped, because a hole would silently shift
// every later point left on the time axis.
const cleanTrack = (values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const out = new Array(values.length);
  for (let i = 0; i < values.length; i += 1) {
    if (!isFiniteNumber(values[i])) return null;
    out[i] = values[i];
  }
  return out;
};

/**
 * The deflicker window converted from source frames into STORED POINTS.
 *
 * Returns the nearest ODD integer to frameWindow / downsampleFactor, floored at
 * 1. Odd because the window is centred — an even window has no middle sample
 * and biases the average by half a point, the same reason the Python constant
 * is documented as "kept odd".
 *
 * A return of 1 means "no window survives at this resolution": each stored
 * point already spans at least the filter's window, so the caller must not draw
 * a comparison.
 */
export function deflickerPointWindow(
  downsampleFactor,
  frameWindow = DEFLICKER_WINDOW_DEFAULT
) {
  const factor = isFiniteNumber(downsampleFactor) && downsampleFactor >= 1
    ? Math.floor(downsampleFactor)
    : 1;
  const span = isFiniteNumber(frameWindow) && frameWindow >= 1 ? frameWindow : 1;
  const exact = span / factor;
  const odd = Math.round((exact - 1) / 2) * 2 + 1;
  return odd < 1 ? 1 : odd;
}

/**
 * Centred moving average with EDGE padding — identical to `_local_mean` in
 * analyzevideo/processing/flicker.py, and the same N-frame arithmetic mean
 * ffmpeg's `mode=am` applies, shifted to sit centred on the raw curve instead
 * of trailing it by half a window (see the file header).
 *
 * Edge padding (repeat the first/last sample) rather than a shrinking window,
 * so the curve keeps full smoothing at both ends instead of flaring back toward
 * the raw signal exactly where a viewer's eye lands first.
 */
export function rollingMean(values, window) {
  const n = values.length;
  const pad = Math.floor(window / 2);
  if (n === 0 || pad < 1) return values.slice();
  const span = pad * 2 + 1;
  const at = (i) => values[i < 0 ? 0 : i > n - 1 ? n - 1 : i];
  const out = new Array(n);
  for (let i = 0; i < n; i += 1) {
    let sum = 0;
    for (let k = -pad; k <= pad; k += 1) sum += at(i + k);
    out[i] = sum / span;
  }
  return out;
}

// [min, max] of one or more series, padded so a curve never sits flush against
// the frame and a perfectly flat track still has a drawable height.
const domainOf = (series) => {
  let min = Infinity;
  let max = -Infinity;
  for (const values of series) {
    for (const v of values) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  const spread = max - min;
  const pad = spread > 0 ? spread * 0.12 : Math.max(Math.abs(max) * 0.05, 1);
  return { min: min - pad, max: max + pad };
};

/**
 * SVG polyline `points` for a series inside a width x height box.
 *
 * The y axis is inverted (SVG grows downward) and values are clamped to the
 * domain, so a caller passing a tighter domain crops rather than drawing
 * outside the viewBox.
 */
export function toPolylinePoints(values, domain, width, height) {
  const n = values.length;
  if (n === 0) return '';
  const range = domain.max - domain.min || 1;
  const step = n > 1 ? width / (n - 1) : 0;
  const parts = new Array(n);
  for (let i = 0; i < n; i += 1) {
    const t = (values[i] - domain.min) / range;
    const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
    const x = n > 1 ? i * step : width / 2;
    parts[i] = `${x.toFixed(2)},${((1 - clamped) * height).toFixed(2)}`;
  }
  return parts.join(' ');
}

/**
 * The analyzer's blocked "flicker left after the fix" series FOR ONE WINDOW, or
 * null if it did not store one for that window.
 *
 * Never derived, never interpolated between windows, never substituted from a
 * neighbouring window (see the header): the alternative to the right series is
 * NO line, because a wrong one is indistinguishable from a right one on screen.
 *
 * A length that disagrees with the measured track is treated as absent too. The
 * chart maps array index to time, so 59 points stretched across 60 would re-time
 * the estimate rather than fail visibly.
 */
const afterSeriesForWindow = (luminanceTrack, selectedWindow, length) => {
  const byWindow = luminanceTrack.flickerPercentAfterByWindow;
  // Rejecting arrays matters: `['a','b'][ '11' ]` is a silent undefined, but an
  // array of series would index positionally and hand back window 0's data.
  const keyed = byWindow && typeof byWindow === 'object' && !Array.isArray(byWindow)
    ? cleanTrack(byWindow[String(selectedWindow)])
    : null;
  if (keyed && keyed.length === length) return keyed;

  // The pre-Strength alias. It is the DEFAULT window's series and nothing else,
  // so it may only stand in when the default is what was asked for — a client
  // deployed ahead of the analyzer still gets its default right, and gets no
  // line at all (rather than a mislabelled one) at Light or Strong.
  if (selectedWindow !== DEFLICKER_WINDOW_DEFAULT) return null;
  const legacy = cleanTrack(luminanceTrack.flickerPercentAfter);
  return legacy && legacy.length === length ? legacy : null;
};

/**
 * Everything the card needs to draw the flicker proof, or a reason it cannot.
 *
 * @param {object} luminanceTrack video.luminanceTrack as stored by #402.
 * @param {number} frameWindow the CORRECTION window the user picked, in source
 *   frames (the draft's `deflickerWindow`). Anything unrecognised — including
 *   the absent field every pre-Strength draft has — falls back to
 *   DEFLICKER_WINDOW_DEFAULT. The MEASUREMENT window is unaffected by it.
 * @returns {{mode: 'comparison'|'residual'|'unavailable', reason: string|null, ...}}
 *
 * mode:
 *   'comparison'  — before/after luminance curves. Drawn only when the record
 *                   PROVES one stored point per frame: downsampleFactor 1 and
 *                   sourceFrameCount equal to the point count.
 *   'residual'    — the stored per-block flicker percentage. Used at every
 *                   coarser resolution, where a "before" curve would be
 *                   part-smoothed by the storage format rather than by the
 *                   filter (see the file header). It is a DIFFERENT QUANTITY on
 *                   a different axis, so the caller must relabel the chart, not
 *                   just swap the data in. `flickerLostToStorage` ('some' |
 *                   'all' | 'unknown') says which regime it is, so the copy can
 *                   make the weaker or the stronger claim — and 'unknown' tells
 *                   the caller it may not quote a frames-per-point number.
 *                   `flickerPercentAfter` is the analyzer's prediction of the
 *                   flicker LEFT after the fix AT THE SELECTED WINDOW, on the
 *                   same axis — or null when the analyzer stored nothing for
 *                   that window, which must render exactly as a pre-#405 record
 *                   does: one line, no estimate. When it is non-null it always
 *                   describes `frameWindow`, so the caller may label it with
 *                   that and nothing else.
 *   'unavailable' — nothing drawable. `reason` says which:
 *                     'no-track'   the project predates #402, or analysis has
 *                                  not landed yet
 *                     'too-short'  fewer than MIN_TRACK_POINTS samples
 *                     'too-coarse' not provably full rate AND no usable
 *                                  flickerPercent track to fall back to
 */
export function buildFlickerPreview(luminanceTrack, frameWindow) {
  const selectedWindow = normalizeDeflickerWindow(frameWindow);

  const unavailable = (reason) => ({
    mode: 'unavailable',
    reason,
    before: null,
    after: null,
    flickerPercent: null,
    flickerPercentAfter: null,
    downsampleFactor: null,
    pointWindow: null,
    effectiveFrameWindow: null,
    frameWindow: selectedWindow,
    sourceFrameCount: null,
    flickerLostToStorage: null,
    domain: null,
  });

  if (!luminanceTrack || typeof luminanceTrack !== 'object') return unavailable('no-track');

  const before = cleanTrack(luminanceTrack.luminance);
  if (!before) return unavailable('no-track');
  if (before.length < MIN_TRACK_POINTS) return unavailable('too-short');

  // `downsampleFactor` is a CLAIM the document makes; the stored point count
  // against `sourceFrameCount` is a MEASUREMENT of what actually happened. Take
  // the larger of the two, because 1 is the single value that unlocks the
  // two-curve comparison — and the comparison is the one mode whose copy
  // asserts "every frame of this shoot was measured". A record that omits or
  // under-reports its factor must not win that assertion by default.
  const rawFactor = luminanceTrack.downsampleFactor;
  const statedFactor = isFiniteNumber(rawFactor) && rawFactor >= 1
    ? Math.floor(rawFactor)
    : null;
  const sourceFrameCount = isFiniteNumber(luminanceTrack.sourceFrameCount)
    && luminanceTrack.sourceFrameCount >= 1
    ? Math.round(luminanceTrack.sourceFrameCount)
    : null;
  const measuredFactor = sourceFrameCount === null
    ? null
    : Math.max(1, Math.ceil(sourceFrameCount / before.length));
  const downsampleFactor = Math.max(statedFactor ?? 1, measuredFactor ?? 1);
  const pointWindow = deflickerPointWindow(downsampleFactor, selectedWindow);

  // Full rate has to be PROVEN, not assumed: one stored point per frame means
  // the source frame count and the point count agree. A record that cannot
  // prove it (no `sourceFrameCount`) falls through to the residual chart, which
  // under-claims rather than lies.
  const isFullRate = downsampleFactor === 1 && sourceFrameCount === before.length;

  const common = {
    reason: null,
    downsampleFactor,
    pointWindow,
    effectiveFrameWindow: pointWindow * downsampleFactor,
    frameWindow: selectedWindow,
    sourceFrameCount,
    // 'all' once a stored block spans the whole measurement window (the stored
    // curve IS the filtered curve), 'some' below that, 'none' at proven full
    // rate, and 'unknown' when the record is too incomplete to say — the UI must
    // not quote a frames-per-point number it only guessed.
    //
    // Measured against the FIXED window on purpose: this describes what the
    // STORAGE FORMAT did to the track, which is a property of the record and
    // cannot change because the user moved a control.
    flickerLostToStorage: isFullRate
      ? 'none'
      : downsampleFactor >= FLICKER_MEASURE_WINDOW_FRAMES
        ? 'all'
        : downsampleFactor === 1 ? 'unknown' : 'some',
  };

  // Both gates must hold, and they fail for different reasons (see the file
  // header). The product rule — the plotted "before" has to be the real
  // per-frame track — is the binding one today; the maths rule is what would
  // catch a future MAX_TRACK_POINTS or window change that made a converted
  // window collapse at a resolution the product rule still allowed.
  if (isFullRate && pointWindow >= MIN_COMPARISON_POINT_WINDOW) {
    const after = rollingMean(before, pointWindow);
    return {
      ...common,
      mode: 'comparison',
      before,
      after,
      flickerPercent: null,
      flickerPercentAfter: null,
      domain: domainOf([before, after]),
    };
  }

  // Too coarse for an honest comparison. Fall back to the residual track, which
  // survives blocking because the analyzer measured it at full rate.
  // `flickerLost` distinguishes the two coarse regimes for the UI copy: below
  // the filter window the storage has taken SOME of the flicker, at or above it
  // the stored curve is already the filtered curve.
  const flickerPercent = cleanTrack(luminanceTrack.flickerPercent);
  if (!flickerPercent || flickerPercent.length !== before.length) {
    return { ...unavailable('too-coarse'), ...common, reason: 'too-coarse' };
  }

  // The analyzer's prediction of the flicker LEFT after the fix at the SELECTED
  // strength, passed straight through — never derived here (see the file
  // header). Absent for any window the analyzer did not precompute, which is
  // every window on a project analyzed before the Strength control shipped:
  // one line, exactly as before.
  const flickerPercentAfter = afterSeriesForWindow(
    luminanceTrack,
    selectedWindow,
    flickerPercent.length
  );

  return {
    ...common,
    mode: 'residual',
    before: null,
    after: null,
    flickerPercent,
    flickerPercentAfter,
    // Anchored at zero: "no flicker" is a meaningful floor here, and a
    // min-anchored axis would magnify a dead-flat 0.02% track into a mountain
    // range. The detection threshold stays inside the frame so the reference
    // line is always visible. Both series share the axis — the predicted line
    // normally sits below the measured one, but the domain must not depend on
    // that holding for every block of every shoot.
    domain: {
      min: 0,
      max: (flickerPercentAfter || []).concat(flickerPercent).reduce(
        (m, v) => (v > m ? v : m),
        FLICKER_DETECT_THRESHOLD_PERCENT
      ) * 1.15,
    },
  };
}
